import type { WorldCardInfo, WorldCardLane } from '@/types/viverse-world-card'

// Flight serializes an undefined property as "$undefined" instead of omitting it.
const omitUndefined = <T extends object>(value: T) =>
  Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T

export const toWorldCardRoom = (room: WorldCardInfo): WorldCardInfo =>
  omitUndefined({
    id: room.id,
    title: room.title,
    image: room.image,
    hubs_url: room.hubs_url,
    external_link: room.external_link,
    content_type: room.content_type && { code: room.content_type.code },
    first_public_date: room.first_public_date,
    last_updated_at: room.last_updated_at,
    is_exclusive: room.is_exclusive,
    is_selected: room.is_selected,
    is_hot: room.is_hot,
    is_top: room.is_top,
    is_voucher_room: room.is_voucher_room,
    is_live: room.is_live,
    is_liked: room.is_liked,
    view_count: room.view_count,
    owner:
      room.owner &&
      omitUndefined({
        display_name: room.owner.display_name,
        community_user_id: room.owner.community_user_id,
        viverse_pro: room.owner.viverse_pro,
        verified_creator: room.owner.verified_creator,
      }),
    vod_id: room.vod_id,
    vod_duration: room.vod_duration,
    vod_last_update_date: room.vod_last_update_date,
    trailer_videos: room.trailer_videos?.slice(0, 1).map(({ vod_id }) => ({ vod_id })),
    preferred_devices: room.preferred_devices,
  })

export const toWorldCardLane = (lane: WorldCardLane): WorldCardLane =>
  omitUndefined({
    id: lane.id,
    title: lane.title,
    is_ranked: lane.is_ranked,
    is_device_filter_enabled: lane.is_device_filter_enabled,
    preferred_devices: lane.preferred_devices,
    description: lane.description,
    // The client treats rooms as nullable and the device filter drops null entries.
    rooms: lane.rooms?.filter(Boolean).map(toWorldCardRoom) ?? [],
  })

/** Returns the text to display and whether it was cut; counts code points so emoji are not split. */
export const truncateDescription = (text: string, maxLength: number) => {
  const chars = Array.from(text)
  return chars.length > maxLength
    ? { text: chars.slice(0, maxLength).join('').trimEnd(), isTruncated: true }
    : { text, isTruncated: false }
}
